// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Skein — the three modes (principle #14). The RULE that ends a run is the axis, not just
// the bead count: Loom is a single calm web with no pressure at all (untangle at leisure,
// score = fewest moves); Cascade is a THROUGHPUT run — a stream of small webs against a
// clock, bank as many as you can; Snarl is one big web with a hard MOVE BUDGET — run out
// of drags before it is clean and the run is over. Those are structurally different games,
// not one game with a dial.
//
// The host's chosen mode travels FROZEN inside the round start (rematch roundOpts); a guest
// renders state().hostOpts. An id off the wire is always modeOf()-validated (Object.hasOwn,
// so a wire key can never resolve a prototype member) — unknown falls back to Loom.

export type ModeId = 'loom' | 'cascade' | 'snarl';

export interface Mode {
  readonly id: ModeId;
  readonly name: string;
  readonly blurb: string;
  /** Number of beads in a web. */
  readonly n: number;
  /** Edge density: target thread count ≈ round(n * density). */
  readonly density: number;
  /** Cascade only: a run is a stream of fresh webs. */
  readonly stream: boolean;
  /** Cascade only: run time limit in ms (0 = no clock). */
  readonly limitMs: number;
  /** Snarl only: max moves before the run ends unsolved (0 = unlimited). */
  readonly budget: number;
}

export const MODES: Record<ModeId, Mode> = {
  loom: {
    id: 'loom',
    name: 'Loom',
    blurb: 'One web, no clock, no limit. Drag the beads until nothing crosses. A clean, calm untangle — fewer moves is a better solve.',
    n: 10,
    density: 1.5,
    stream: false,
    limitMs: 0,
    budget: 0,
  },
  cascade: {
    id: 'cascade',
    name: 'Cascade',
    blurb: 'A stream of small webs against a 75-second clock. Untangle one and the next deals instantly — bank as many as you can.',
    n: 7,
    density: 1.5,
    stream: true,
    limitMs: 75_000,
    budget: 0,
  },
  snarl: {
    id: 'snarl',
    name: 'Snarl',
    blurb: 'One big tangle and a limited number of moves. Plan each drag — run out before it is clean and the run is over.',
    n: 14,
    density: 1.55,
    stream: false,
    limitMs: 0,
    budget: 21, // ceil(n * 1.5) — an efficient solver makes it, a flailer does not
  },
};

export const MODE_IDS: ModeId[] = ['loom', 'cascade', 'snarl'];

export const DEFAULT_MODE: Mode = MODES.loom;

/** Resolve a (possibly untrusted, off-the-wire) id to a Mode; unknown → Loom. */
export function modeOf(id: unknown): Mode {
  if (typeof id === 'string' && Object.hasOwn(MODES, id)) {
    return MODES[id as ModeId];
  }
  return DEFAULT_MODE;
}
